/**
 * NotificationsBell.js
 * ====================
 *
 * Bell icon + dropdown drawer that lives in the global Header to the
 * left of the user-account chip.
 *
 * Architecture:
 *   * Reads ``state.notifications`` (items + unreadCount + sseStatus)
 *   * On mount, dispatches ``fetchList`` to seed the cache and
 *     ``sseConnect`` to open the EventSource. Both are idempotent at
 *     the saga level.
 *   * Per-row click navigates to the notification's ``link`` AND
 *     marks the row read.
 *   * Footer has "Mark all as read" + "Notifications settings"
 *     (deep-link to /settings/notifications).
 *
 * Styling mirrors the user-account Menu PaperProps so visually the
 * bell drawer feels like a sibling of the account menu.
 */

import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import useRealtimeRefresh from '../../realtime/useRealtimeRefresh';
import { useDispatch, useSelector } from 'react-redux';
import { useNavigate } from 'react-router-dom';

import {
  Badge, Box, Chip, CircularProgress, Divider,
  IconButton, ListItemIcon, MenuItem, Menu, Stack, Tooltip,
  Typography, alpha, useTheme,
} from '@mui/material';
import NotificationsNoneIcon from '@mui/icons-material/NotificationsNone';
import NotificationsActiveIcon from '@mui/icons-material/NotificationsActive';
import InfoOutlinedIcon from '@mui/icons-material/InfoOutlined';
import CheckCircleOutlineIcon from '@mui/icons-material/CheckCircleOutline';
import WarningAmberOutlinedIcon from '@mui/icons-material/WarningAmberOutlined';
import ErrorOutlineIcon from '@mui/icons-material/ErrorOutline';
import SettingsIcon from '@mui/icons-material/Settings';
import DoneAllIcon from '@mui/icons-material/DoneAll';
import OpenInFullIcon from '@mui/icons-material/OpenInFull';

import {
  fetchList, markRead, markAllRead, sseConnect,
} from '../../redux/actions/notifications';
import { isExternalLink } from '../../utils/notificationLink';


// Severity -> icon + color (mirrors the YAML severities list).
const SEVERITY_META = {
  info:     { Icon: InfoOutlinedIcon,        toneKey: 'info'    },
  success:  { Icon: CheckCircleOutlineIcon,  toneKey: 'success' },
  warning:  { Icon: WarningAmberOutlinedIcon, toneKey: 'warning' },
  critical: { Icon: ErrorOutlineIcon,        toneKey: 'error'   },
};

const _toneColor = (theme, toneKey) => {
  switch (toneKey) {
    case 'info':    return theme.palette.info.main;
    case 'success': return theme.palette.success.main;
    case 'warning': return theme.palette.warning.main;
    case 'error':   return theme.palette.error.main;
    default:        return theme.palette.text.secondary;
  }
};

const _relativeTime = (iso) => {
  if (!iso) return '';
  const t = new Date(iso).getTime();
  const dt = (Date.now() - t) / 1000;
  if (dt < 60) return 'just now';
  if (dt < 3600) return `${Math.floor(dt / 60)}m ago`;
  if (dt < 86400) return `${Math.floor(dt / 3600)}h ago`;
  if (dt < 86400 * 7) return `${Math.floor(dt / 86400)}d ago`;
  return new Date(iso).toLocaleDateString();
};


const NotificationsBell = () => {
  const theme = useTheme();
  const dispatch = useDispatch();
  const navigate = useNavigate();

  const items = useSelector((s) => s.notifications?.items) || [];
  const unreadCount = useSelector((s) => s.notifications?.unreadCount || 0);
  const sseStatus = useSelector((s) => s.notifications?.sseStatus || 'idle');
  const isLoading = useSelector((s) => s.notifications?.isLoading);
  const lastReceived = useSelector((s) => s.notifications?.lastReceived);
  const receivedSeq = useSelector((s) => s.notifications?.receivedSeq || 0);

  const [anchorEl, setAnchorEl] = useState(null);
  const open = Boolean(anchorEl);

  // Inline toast: the newest live-pushed notification, shown inside the
  // bell pill for a few seconds (auto-hides; hover pauses the timer).
  const [toast, setToast] = useState(null);
  const hideTimer = useRef(null);

  const stopHideTimer = useCallback(() => {
    if (hideTimer.current) {
      clearTimeout(hideTimer.current);
      hideTimer.current = null;
    }
  }, []);

  const clearToast = useCallback(() => {
    stopHideTimer();
    setToast(null);
  }, [stopHideTimer]);

  // Surface each live arrival. Guard against reconnect replays of old
  // rows: only unread rows created in the last couple of minutes show.
  useEffect(() => {
    if (!receivedSeq || !lastReceived || lastReceived.read_at) return;
    const createdMs = new Date(lastReceived.created_at || Date.now()).getTime();
    if (Date.now() - createdMs > 2 * 60 * 1000) return;
    setToast(lastReceived);
    stopHideTimer();
    hideTimer.current = setTimeout(() => setToast(null), 6500);
  }, [receivedSeq]); // eslint-disable-line react-hooks/exhaustive-deps

  useEffect(() => stopHideTimer, [stopHideTimer]);

  const pauseToastHide = () => stopHideTimer();
  const resumeToastHide = () => {
    if (toast && !hideTimer.current) {
      hideTimer.current = setTimeout(() => setToast(null), 2500);
    }
  };

  // Workspace-prefixed and legacy link shapes are resolved by the
  // router (WorkspaceDeepLink / LegacyPathRedirect in App.js); absolute
  // URLs (admin announcements) must leave the SPA.
  const openLink = useCallback((link) => {
    if (!link) return;
    if (isExternalLink(link)) window.open(link, '_blank', 'noopener');
    else navigate(link);
  }, [navigate]);

  const handleToastClick = () => {
    const n = toast;
    clearToast();
    if (n?.notification_id && !n.read_at) dispatch(markRead(n.notification_id));
    openLink(n?.link);
  };

  // Boot: seed the cache + open the realtime channel once. Sagas are
  // idempotent so dispatching the connect a second time is a no-op.
  // Notifications now arrive live over the WebSocket (delivered to the bell
  // by the realtime saga as NOTIFICATION_RECEIVED), so there's no polling.
  useEffect(() => {
    dispatch(fetchList({ per_page: 50 }));
    dispatch(sseConnect());
  }, [dispatch]);

  // Reconcile on (re)connect: if the socket dropped and came back, re-seed
  // the latest 50 so the unread badge can't drift while we were offline.
  // (The RECEIVED dedupe by notification_id prevents double-counting.) The
  // matcher is false, so this only fires on the hook's 'open' reconnect path.
  useRealtimeRefresh(
    () => false,
    () => dispatch(fetchList({ per_page: 50 })),
  );

  const handleOpen = (event) => {
    clearToast(); // they're about to see the full list anyway
    setAnchorEl(event.currentTarget);
    // Refresh on open in case SSE missed something while disconnected.
    dispatch(fetchList({ per_page: 50 }));
  };

  const handleClose = () => setAnchorEl(null);

  const handleRowClick = (n) => {
    if (n.notification_id && !n.read_at) {
      dispatch(markRead(n.notification_id));
    }
    openLink(n.link);
    handleClose();
  };

  const handleMarkAllRead = () => {
    dispatch(markAllRead());
  };

  const handleSettings = () => {
    handleClose();
    navigate('/settings/notifications');
  };

  const handleViewAll = () => {
    handleClose();
    navigate('/notifications');
  };

  // Visible items: cap to ~50 in the drawer; the user can open the
  // full settings page for history. Newest first is the BE order.
  const visible = useMemo(() => items.slice(0, 50), [items]);

  const toastMeta = SEVERITY_META[toast?.severity] || SEVERITY_META.info;
  const ToastIcon = toastMeta.Icon;
  const toastColor = _toneColor(theme, toastMeta.toneKey);
  const dark = theme.palette.mode === 'dark';

  return (
    <>
      {/* Pill (sibling of the account chip). A freshly arrived
          notification expands inline out of it for a few seconds. */}
      <Box
        onMouseEnter={pauseToastHide}
        onMouseLeave={resumeToastHide}
        sx={{
          display: 'flex',
          alignItems: 'center',
          height: 36,
          pl: toast ? 1.25 : 0.375,
          pr: 0.375,
          border: '1px solid',
          borderColor: toast ? alpha(toastColor, 0.45) : 'divider',
          borderRadius: 999,
          bgcolor: toast ? alpha(toastColor, dark ? 0.10 : 0.05) : 'transparent',
          // No overflow:hidden here — it would clip the unread Badge,
          // which overhangs the bell icon. The toast box clips itself.
          transition: 'background-color 200ms ease, border-color 200ms ease, padding 240ms ease',
          ...(!toast && {
            '&:hover': {
              bgcolor: alpha(theme.palette.primary.main, dark ? 0.14 : 0.06),
              borderColor: alpha(theme.palette.primary.main, 0.4),
            },
          }),
        }}
      >
        <Box
          onClick={handleToastClick}
          role={toast ? 'status' : undefined}
          aria-live="polite"
          sx={{
            display: 'flex',
            alignItems: 'center',
            gap: 1,
            minWidth: 0,
            maxWidth: toast ? { xs: 190, sm: 340 } : 0,
            opacity: toast ? 1 : 0,
            overflow: 'hidden',
            cursor: toast ? 'pointer' : 'default',
            transition: 'max-width 280ms ease, opacity 220ms ease',
          }}
        >
          <ToastIcon sx={{ fontSize: 16, color: toastColor, flexShrink: 0 }} />
          <Typography
            sx={{
              fontSize: 12.5, fontWeight: 600, lineHeight: 1.3,
              whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis',
              minWidth: 0, mr: 0.75,
            }}
          >
            {toast?.title || 'Notification'}
          </Typography>
        </Box>

        <Tooltip title={
          sseStatus === 'open' ? 'Notifications (live)'
          : sseStatus === 'connecting' ? 'Notifications (connecting)'
          : 'Notifications'
        }>
          <IconButton
            size="small"
            onClick={handleOpen}
            aria-label="Notifications"
            sx={{ color: theme.palette.text.primary }}
          >
            <Badge
              color="error"
              badgeContent={unreadCount}
              max={99}
              invisible={unreadCount === 0}
            >
              {unreadCount > 0
                ? <NotificationsActiveIcon sx={{ fontSize: 20 }} />
                : <NotificationsNoneIcon sx={{ fontSize: 20 }} />}
            </Badge>
          </IconButton>
        </Tooltip>
      </Box>

      <Menu
        anchorEl={anchorEl}
        id="notifications-menu"
        open={open}
        onClose={handleClose}
        PaperProps={{
          elevation: 0,
          sx: {
            overflow: 'visible',
            filter: 'drop-shadow(0px 2px 8px rgba(0,0,0,0.32))',
            mt: 1.5,
            width: 380,
            maxWidth: '90vw',
            '&::before': {
              content: '""',
              display: 'block',
              position: 'absolute',
              top: 0,
              right: 14,
              width: 10,
              height: 10,
              bgcolor: 'background.paper',
              transform: 'translateY(-50%) rotate(45deg)',
              zIndex: 0,
            },
          },
        }}
        transformOrigin={{ horizontal: 'right', vertical: 'top' }}
        anchorOrigin={{ horizontal: 'right', vertical: 'bottom' }}
      >
        {/* Header */}
        <Box sx={{
          display: 'flex', alignItems: 'center', justifyContent: 'space-between',
          px: 2, py: 1.25,
        }}>
          <Typography variant="subtitle2" sx={{ fontWeight: 700 }}>
            Notifications
          </Typography>
          {unreadCount > 0 && (
            <Tooltip title="Mark all as read">
              <IconButton size="small" onClick={handleMarkAllRead}>
                <DoneAllIcon fontSize="small" />
              </IconButton>
            </Tooltip>
          )}
        </Box>
        <Divider />

        {/* Body */}
        <Box sx={{ maxHeight: 480, overflowY: 'auto' }}>
          {isLoading && visible.length === 0 && (
            <Box sx={{ display: 'flex', justifyContent: 'center', py: 4 }}>
              <CircularProgress size={20} />
            </Box>
          )}
          {!isLoading && visible.length === 0 && (
            <Box sx={{ px: 2, py: 4, textAlign: 'center' }}>
              <Typography variant="body2" color="text.secondary">
                You're all caught up.
              </Typography>
            </Box>
          )}
          {visible.map((n) => {
            const meta = SEVERITY_META[n.severity] || SEVERITY_META.info;
            const SevIcon = meta.Icon;
            const sevColor = _toneColor(theme, meta.toneKey);
            const unread = !n.read_at;
            return (
              <MenuItem
                key={n.notification_id}
                onClick={() => handleRowClick(n)}
                sx={{
                  alignItems: 'flex-start',
                  whiteSpace: 'normal',
                  borderLeft: `3px solid ${unread ? sevColor : 'transparent'}`,
                  bgcolor: unread ? alpha(sevColor, 0.04) : 'transparent',
                  '&:hover': {
                    bgcolor: alpha(sevColor, 0.08),
                  },
                  py: 1.25,
                  px: 1.5,
                }}
              >
                <ListItemIcon sx={{
                  minWidth: 28, mt: 0.25, color: sevColor,
                }}>
                  <SevIcon fontSize="small" />
                </ListItemIcon>
                <Box sx={{ flex: 1, minWidth: 0 }}>
                  <Stack direction="row" spacing={1} alignItems="center"
                         sx={{ mb: 0.25 }}>
                    <Typography
                      variant="body2"
                      sx={{
                        fontWeight: unread ? 700 : 500,
                        flex: 1, minWidth: 0,
                        whiteSpace: 'nowrap',
                        overflow: 'hidden',
                        textOverflow: 'ellipsis',
                      }}
                    >
                      {n.title}
                    </Typography>
                    <Typography variant="caption" color="text.secondary"
                                sx={{ flexShrink: 0 }}>
                      {_relativeTime(n.created_at)}
                    </Typography>
                  </Stack>
                  <Typography
                    variant="caption"
                    color="text.secondary"
                    sx={{
                      display: '-webkit-box',
                      WebkitLineClamp: 2,
                      WebkitBoxOrient: 'vertical',
                      overflow: 'hidden',
                    }}
                  >
                    {n.body}
                  </Typography>
                  {n.category && (
                    <Chip
                      label={n.category}
                      size="small"
                      variant="outlined"
                      sx={{
                        mt: 0.5,
                        height: 16,
                        fontSize: 9,
                        '& .MuiChip-label': { px: 0.75 },
                      }}
                    />
                  )}
                </Box>
              </MenuItem>
            );
          })}
        </Box>

        <Divider />

        {/* Footer */}
        <MenuItem onClick={handleViewAll}>
          <ListItemIcon>
            <OpenInFullIcon fontSize="small" />
          </ListItemIcon>
          <Typography variant="body2">View all notifications</Typography>
        </MenuItem>
        <MenuItem onClick={handleSettings}>
          <ListItemIcon>
            <SettingsIcon fontSize="small" />
          </ListItemIcon>
          <Typography variant="body2">Notification settings</Typography>
        </MenuItem>
      </Menu>
    </>
  );
};

export default NotificationsBell;
